import React, { useState, useCallback } from 'react';
import { Sparkles, ShieldCheck, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * OptimalPlanCTA — "Maak mijn optimaal stuurplan".
 *
 * Klant-knop die de bestaande plan-generator aanroept. Output is ALTIJD
 * `execution_mode=simulation`. Toont advieskaarten met savings_label,
 * comfort_impact en risk_profile. Voert NIETS echt uit.
 */
export default function OptimalPlanCTA({ detectedDevices = [], sessionId }) {
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState(null);

  const logEvent = useCallback(async (event, meta) => {
    try {
      await fetch(`${API}/track/funnel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, session_id: sessionId || 'mp-plan-cta', meta: meta || {} }),
      });
    } catch { /* no-op */ }
  }, [sessionId]);

  const generate = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      logEvent('optimal_plan_requested', { devices_count: detectedDevices.length });
      const r = await fetch(`${API}/devices/intelligence/plan/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          detected_devices: detectedDevices,
          session_id: sessionId,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || 'plan_failed');
      setPlan(j);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [detectedDevices, sessionId, logEvent]);

  return (
    <div
      className="rounded-2xl border border-emerald-400/30 p-4 space-y-3"
      style={{ background: 'linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)' }}
      data-testid="mp-optimal-plan-cta"
    >
      <div className="flex items-start gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 flex-shrink-0">
          <Sparkles className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
            Maak mijn optimaal stuurplan
          </div>
          <p className="text-[12px] text-white/65 leading-snug mt-0.5">
            Powersure berekent een veilig voorstel op basis van uw apparaten en tarieven.
          </p>
        </div>
      </div>

      <div
        className="rounded-lg border border-emerald-400/20 bg-emerald-500/[0.06] p-2.5 flex items-start gap-2"
        data-testid="mp-plan-safety-note"
      >
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />
        <p className="text-[11.5px] text-white/75 leading-relaxed">
          <span className="text-emerald-200 font-medium">Er wordt nog niets automatisch geschakeld.</span>{' '}
          Powersure maakt nu alleen een veilig voorstel.
        </p>
      </div>

      <button
        type="button"
        onClick={generate}
        disabled={loading}
        data-testid="mp-optimal-plan-button"
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm transition-colors disabled:opacity-60"
      >
        <Sparkles className="w-3.5 h-3.5" />
        {loading ? 'Plan berekenen…' : 'Maak mijn optimaal stuurplan'}
      </button>

      {err && (
        <div className="text-xs text-amber-300 inline-flex items-center gap-1" data-testid="mp-plan-error">
          <AlertCircle className="w-3 h-3" /> {err}
        </div>
      )}

      {plan && (
        <div className="space-y-2 pt-1" data-testid="mp-plan-result">
          <div className="text-[12px] text-white/65 leading-snug">
            Plan {plan.plan_id?.slice(0, 12)}… ·{' '}
            <span className="text-emerald-300">{plan.execution_mode || 'simulation'}</span> · {plan.actions?.length || 0} voorstellen
          </div>
          {(plan.actions || []).length === 0 ? (
            <p className="text-[12px] text-white/65">
              Geen acties mogelijk — voeg eerst meer apparaten toe.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {(plan.actions || []).map((a, i) => (
                <li
                  key={i}
                  className="rounded-lg border border-white/10 bg-white/[0.04] p-2.5"
                  data-testid={`mp-plan-action-${i}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-0.5">
                    <div className="text-[12.5px] font-semibold text-white">
                      {a.action?.replace(/_/g, ' ')}
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-400/30 bg-emerald-500/10 text-emerald-300">
                      {a.savings_label}
                    </span>
                  </div>
                  <div className="text-[11px] text-white/60">
                    Apparaat: {a.device_kind?.replace(/_/g, ' ')} · Comfort: {a.comfort_impact} · Risico: {a.risk_profile}
                  </div>
                  <div className="text-[10.5px] text-emerald-300/80 mt-0.5">
                    Automatisch mogelijk na uw expliciete toestemming · nu alleen simulatie.
                  </div>
                </li>
              ))}
            </ul>
          )}
          {plan.safety_warnings?.length > 0 && (
            <div className="text-[11px] text-amber-300" data-testid="mp-plan-warnings">
              Let op: {plan.safety_warnings.join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
